/**
 * Componente KPICardSkeleton
 * 
 * Skeleton loading para o componente KPICard.
 * Exibe um placeholder animado enquanto os dados estão carregando.
 * 
 * @module components/Dashboard/KPISection/KPICardSkeleton
 */

import React from 'react';
import PropTypes from 'prop-types';
import Card from '../../UI/Card';
import CardContent from '../../UI/CardContent';

/**
 * Componente de Skeleton para KPICard
 * 
 * Exibe um placeholder animado com efeito de "shimmer" (brilho)
 * para indicar que os dados estão carregando.
 * 
 * @param {object} props - Propriedades do componente
 * @param {string} [props.className] - Classes CSS adicionais
 * @param {string} [props.testId] - ID para testes (data-testid)
 * 
 * @returns {JSX.Element} Componente de skeleton
 * 
 * @example
 * ```jsx
 * {isLoading ? (
 *     <KPICardSkeleton />
 * ) : (
 *     <KPICard {...kpiProps} />
 * )}
 * ```
 */
const KPICardSkeleton = ({ className = '', testId = null }) => {
    return (
        <Card
            className={`border border-gray-100 mb-2 ${className}`}
            role="status"
            aria-label="Carregando indicador de performance"
            data-testid={testId || 'kpi-card-skeleton'}
        >
            <CardContent className="flex flex-col p-6">
                {/* Header com label e ícone skeleton */}
                <div className="flex items-center justify-between mb-2">
                    {/* Label skeleton */}
                    <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                    {/* Ícone skeleton */}
                    <div className="h-8 w-8 bg-gray-200 rounded-full animate-pulse" />
                </div>

                {/* Valor skeleton */}
                <div className="h-12 w-24 bg-gray-200 rounded mt-1 animate-pulse" />

                {/* Descrição skeleton */}
                <div className="h-3 w-40 bg-gray-200 rounded mt-1 animate-pulse" />
            </CardContent>
        </Card>
    );
};

KPICardSkeleton.propTypes = {
    /** Classes CSS adicionais */
    className: PropTypes.string,
    /** ID para testes (data-testid) */
    testId: PropTypes.string,
};

// Default props removidos - usando default parameters na função

export default KPICardSkeleton;

